import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'usehooks-ts'
import { SetQuery, QueryParamConfig } from 'use-query-params'

import {
  AsyncSelect,
  DateRange,
  Filter,
  Flex,
  Icon,
  Select,
  SelectOption,
  TableSearchAndFilters
} from '@percent/lemonade'
import { useQueryList } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import {
  OpportunityStatus,
  TimeLogSearchField,
  VolunteeringTimeLogType
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { dayJS } from '@percent/partner-dashboard/common/library/utility/date'
import styles from './TimeLogsSearchAndFilters.module.scss'

type TimeLogsSearchAndFiltersProps = {
  onFilterChange: (filter: {
    opportunityId?: string
    accountId?: string
    type?: VolunteeringTimeLogType
    dateFrom?: string
    dateTo?: string
  }) => void
}

type QueryParam<T> = QueryParamConfig<T | null | undefined, T | null | undefined>

type DateRangeQueryParams = {
  startDate: QueryParam<string>
  endDate: QueryParam<string>
}

export function TimeLogsSearchAndFilters({ onFilterChange }: Readonly<TimeLogsSearchAndFiltersProps>) {
  const { t } = useTranslation()
  const { workplaceGivingService } = useServices()
  const [searchField, setSearchField] = useState<TimeLogSearchField>('opportunity')
  const [query, setQuery] = useState('')
  const [asyncSelectKey, setAsyncSelectKey] = useState(0)
  const [selectedType, setSelectedType] = useState<VolunteeringTimeLogType | ''>('')
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [dateFrom, setDateFrom] = useState<string | undefined>()
  const [dateTo, setDateTo] = useState<string | undefined>()
  const [selectedSearchValue, setSelectedSearchValue] = useState<{ opportunityId?: string; accountId?: string }>({})

  const debouncedQuery = useDebounce(query, 300)
  const shouldSearch = debouncedQuery.length >= 2

  const [{ data: opportunitiesData, isLoading: isSearchingOpportunities }, { query: searchOpportunities }] =
    useQueryList(
      workplaceGivingService.getVolunteeringOpportunitiesList,
      {
        query: '',
        pageSize: 10,
        status: [OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED, OpportunityStatus.CANCELLED]
      },
      false
    )

  const [{ data: employeesData, isLoading: isSearchingEmployees }, { query: searchEmployees }] = useQueryList(
    workplaceGivingService.getEmployeesList,
    {
      filter: '',
      pageSize: 10
    },
    false
  )

  useEffect(() => {
    if (shouldSearch && searchField === 'opportunity') {
      searchOpportunities({
        query: debouncedQuery,
        pageSize: 10,
        status: [OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED, OpportunityStatus.CANCELLED]
      })
    }

    if (shouldSearch && searchField === 'employee') {
      searchEmployees({
        filter: debouncedQuery,
        pageSize: 10
      })
    }
  }, [shouldSearch, debouncedQuery, searchField, searchOpportunities, searchEmployees])

  const opportunityOptions: SelectOption[] = useMemo(
    () => (opportunitiesData ?? []).map(opp => ({ label: opp.name, value: opp.id })),
    [opportunitiesData]
  )

  const employeeOptions: SelectOption[] = useMemo(
    () =>
      (employeesData ?? []).map(emp => ({
        label: emp.fullName ? `${emp.fullName} \u2014 ${emp.email}` : emp.email,
        value: emp.id
      })),
    [employeesData]
  )

  const fireFilterChange = useCallback(
    (overrides: {
      search?: { opportunityId?: string; accountId?: string }
      type?: VolunteeringTimeLogType | ''
      dateFrom?: string
      dateTo?: string
      clearDate?: boolean
    }) => {
      const search = overrides.search ?? selectedSearchValue
      const typeVal = overrides.type ?? selectedType
      const from = overrides.clearDate ? undefined : overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom
      const to = overrides.clearDate ? undefined : overrides.dateTo !== undefined ? overrides.dateTo : dateTo

      onFilterChange({
        ...search,
        ...(typeVal && { type: typeVal }),
        ...(from && { dateFrom: from }),
        ...(to && { dateTo: to })
      })
    },
    [selectedSearchValue, selectedType, dateFrom, dateTo, onFilterChange]
  )

  const handleSelect = useCallback(
    (option: SelectOption | null) => {
      if (!option) {
        setSelectedSearchValue({})
        fireFilterChange({ search: {} })

        return
      }

      const search = searchField === 'opportunity' ? { opportunityId: option.value } : { accountId: option.value }

      setSelectedSearchValue(search)
      fireFilterChange({ search })
    },
    [searchField, fireFilterChange]
  )

  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery)
  }, [])

  const searchFieldOptions = useMemo(
    () => [
      { label: t('table.header.opportunity'), value: 'opportunity' },
      { label: t('table.header.employee'), value: 'employee' }
    ],
    [t]
  )

  const handleSearchFieldChange = useCallback(
    (option: { value: string }) => {
      setSearchField(option.value as TimeLogSearchField)
      setQuery('')
      setAsyncSelectKey(prev => prev + 1)
      setSelectedSearchValue({})
      fireFilterChange({ search: {} })
    },
    [fireFilterChange]
  )

  const typeOptions = useMemo(
    () => [
      { label: t('typography.timeLogType.all'), value: '' },
      { label: t('typography.timeLogType.off_platform'), value: 'off_platform' },
      { label: t('typography.timeLogType.on_platform'), value: 'on_platform' }
    ],
    [t]
  )

  const handleTypeFilterChange = useCallback(
    (typeValue: string) => {
      setSelectedType(typeValue as VolunteeringTimeLogType | '')
      fireFilterChange({ type: typeValue as VolunteeringTimeLogType | '' })
    },
    [fireFilterChange]
  )

  const handleDatePickerClick = useCallback(() => {
    setIsDatePickerOpen(prev => !prev)
  }, [])

  const handleDateRangeChange: SetQuery<DateRangeQueryParams> = useCallback(
    (
      changes:
        | Partial<{ startDate: string | null | undefined; endDate: string | null | undefined }>
        | ((latestValues: {
            startDate: string | null | undefined
            endDate: string | null | undefined
          }) => Partial<{ startDate: string | null | undefined; endDate: string | null | undefined }>)
    ) => {
      const params = typeof changes === 'function' ? changes({ startDate: dateFrom, endDate: dateTo }) : changes
      const from = params.startDate ?? undefined
      const to = params.endDate ?? undefined

      setDateFrom(from)
      setDateTo(to)
      fireFilterChange({ dateFrom: from, dateTo: to })
    },
    [fireFilterChange, dateFrom, dateTo]
  )

  const handleClearDate = useCallback(() => {
    setDateFrom(undefined)
    setDateTo(undefined)
    fireFilterChange({ clearDate: true })
  }, [fireFilterChange])

  const isOpportunitySearch = searchField === 'opportunity'
  const hasDateRange = dateFrom && dateTo

  return (
    <TableSearchAndFilters>
      <div className={styles.searchAndFilters}>
        <div className={styles.searchFieldSelect}>
          <Select
            placeholder={t('table.header.opportunity')}
            options={searchFieldOptions}
            defaultValue={searchFieldOptions[0]}
            onChange={handleSearchFieldChange}
            data-testid="time-logs-search-field-select"
          />
        </div>
        <div className={styles.searchInput}>
          <AsyncSelect
            key={asyncSelectKey}
            name="time-logs-search"
            placeholder={isOpportunitySearch ? t('typography.searchOpportunities') : t('typography.searchEmployees')}
            options={isOpportunitySearch ? opportunityOptions : employeeOptions}
            setQuery={handleQueryChange}
            query={query}
            loading={isOpportunitySearch ? isSearchingOpportunities : isSearchingEmployees}
            loadingText={t('typography.searching')}
            noResultsFoundText={query.length >= 2 ? t('typography.noSearchResults') : t('typography.typeAtLeast2Chars')}
            onChange={handleSelect}
            data-testid="time-logs-search"
            clearable
          />
        </div>
        <div className={styles.filtersRow}>
          <Filter
            label={t('table.header.type')}
            options={typeOptions}
            selectedValue={selectedType}
            onChange={handleTypeFilterChange}
            testId="time-logs-type-filter"
          />
          <DateRange
            open={isDatePickerOpen}
            setOpen={setIsDatePickerOpen}
            setQueryParams={handleDateRangeChange}
            queryParams={{ startDate: dateFrom, endDate: dateTo }}
            maxDate={new Date()}
          >
            <button
              onClick={handleDatePickerClick}
              className={styles.dateButton}
              type="button"
              data-testid="time-logs-date-range-trigger"
            >
              <Flex align="center" gap={4}>
                <span>
                  {(hasDateRange && `${dayJS(dateFrom).format('ll')} - ${dayJS(dateTo).format('ll')}`) ||
                    t('table.header.date')}
                </span>
                <Icon name={isDatePickerOpen ? 'chevron-up' : 'chevron-down'} size={3} color="black" />
              </Flex>
            </button>
          </DateRange>
          {hasDateRange && (
            <button onClick={handleClearDate} className={styles.clearDate} type="button">
              {t('typography.clearDate')}
            </button>
          )}
        </div>
      </div>
    </TableSearchAndFilters>
  )
}
